/**
 * Video die tijdens het scrollen van klein naar breed groeit.
 *
 * Het blok zet twee halteplaatsen onder elkaar: een kleine bovenin en een brede eronder.
 * De video staat in de markup in de brede -- zonder JS is dat de enige die er is en staat
 * hij dus gewoon groot op de pagina. Zodra dit script het overneemt verhuist hij naar de
 * kleine halte, wordt die zichtbaar, en rekent GSAP Flip hem terug naar de brede terwijl je
 * scrollt.
 *
 * Flip.fit meet allebei de halteplaatsen en zet het verschil om in breedte, hoogte en
 * verplaatsing. Bewust geen scale: daarmee zou alles wat in het kader staat -- de
 * afspeelknop voorop -- mee uitrekken. Nu houdt de knop zijn eigen maat.
 *
 * De meting geldt voor één schermbreedte, dus na een resize wordt de tijdlijn opnieuw
 * opgebouwd. Dat wachten we even af: tijdens het slepen aan een vensterrand komen die
 * gebeurtenissen bij tientallen tegelijk.
 *
 * De bediening is niet die van de browser maar die van de hero: zodra er wordt
 * afgespeeld krijgt het kader scripts/player.js eraan gehangen, en verschijnt onderin
 * dezelfde balk als onder de showreel -- zie partials/player-bar.blade.php.
 *
 * Maten: resources/styles/common/video-scale.scss.
 */

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Flip } from 'gsap/Flip';

import { minderBeweging } from './hero-motion';
import { setupPlayer } from './player';

gsap.registerPlugin(ScrollTrigger, Flip);

export default function initVideoScale() {
  document.querySelectorAll('[data-video-scale]').forEach((root) => {
    speelknop(root);

    if (! minderBeweging()) {
      groeiMee(root);
    }
  });
}

/**
 * De afspeelknop en de bediening.
 *
 * Aangeslagen op de aanwijzer en niet op `click`, en op het hele beeld en niet alleen op
 * het knopje. Reden: dit kader beweegt. Zolang je scrollt schaalt het mee, en met een
 * scrub loopt het daarna nog even na. Een browser maakt alleen een klik van muis-omlaag en
 * muis-omhoog als die op hetzelfde element landen -- op een bewegend doel lukt dat vaak
 * net niet, en dan gebeurt er niets. Met de aanwijzer vastgezet op het kader komt het
 * loslaten altijd hier terecht, waar het beeld ondertussen ook heen is geschoven.
 *
 * Slepen telt niet mee: verplaatst de aanwijzer meer dan een streepje tussen indrukken en
 * loslaten, dan was het scrollen of selecteren en niet afspelen.
 *
 * Loopt de film eenmaal, dan zet diezelfde klik hem stil en weer aan -- dat is wat je van
 * een video verwacht, en de balk onderin heeft er geen knop voor.
 *
 * Het knopje blijft een echte <button>, zodat toetsenbord en schermlezer hem gewoon
 * kunnen bedienen -- daar komt de klik langs de normale weg binnen.
 *
 * Twee gevallen bij het afspelen zelf, want het sfeerbeeld en de hele video zijn niet
 * altijd twee bestanden.
 *
 * Staat er twee keer hetzelfde bestand -- en dat is op beide pagina's zo -- dan krijgt de
 * video die al draait gewoon zijn geluid en zijn bediening, terug naar het begin. Dat is
 * meteen raak, want het beeld is er al. Zou je in plaats daarvan een tweede element
 * aanzetten, dan haalt de browser hetzelfde bestand nog een keer op en kijk je een paar
 * tellen naar zwart -- alsof de knop niets doet.
 *
 * Zijn het wél twee bestanden, dan komt de hele video ervoor, maar pas als hij kan
 * spelen. Tot dat moment loopt het sfeerbeeld door, zodat er ook dan geen zwart gat valt.
 */
function speelknop(root) {
  const frame = root.querySelector('[data-video-scale-frame]');
  const knop = root.querySelector('[data-video-scale-play]');
  const preview = root.querySelector('[data-video-scale-preview]');
  const video = root.querySelector('[data-video-scale-video]');

  if (! frame || ! knop || ! preview) {
    return;
  }

  let speler = null;

  const start = () => {
    // Speelt hij al, dan is dit een klik op de bediening van de video zelf.
    if (frame.classList.contains('video-scale__frame--speelt')) {
      return;
    }

    frame.classList.add('video-scale__frame--speelt');

    if (! video) {
      preview.loop = false;
      preview.currentTime = 0;

      // Kijken is iets anders dan sfeer maken: nu mag er niets meer wegvallen aan de
      // randen. Bij een film van 16:9 -- zoals deze -- verandert er niets, maar een
      // afwijkend formaat wordt zo niet bijgesneden.
      preview.classList.add('video-scale__media--full');

      // Even stilzetten voordat de speler het overneemt. Die leest de stand af aan de
      // gebeurtenissen van de video, en een video die al draait meldt geen 'play' meer --
      // dan bleef de bediening voor eeuwig op 'aan het laden' staan.
      preview.pause();

      speler = bedien(frame, preview);

      return;
    }

    speler = bedien(frame, video);

    // Het sfeerbeeld blijft eronder liggen tot de echte video beeld heeft.
    video.addEventListener('canplay', () => {
      preview.hidden = true;
      preview.pause();
    }, { once: true });
  };

  /**
   * Stilzetten of verder kijken. De stand komt van de speler zelf; 'loading' telt als
   * spelen, want daar is de bezoeker net op afgegaan.
   */
  const wissel = () => {
    if (! speler) {
      return;
    }

    const stand = frame.dataset.playerState;

    if (stand === 'playing' || stand === 'loading') {
      speler.pause();
    } else {
      speler.play();
    }
  };

  const aanslag = () => (frame.classList.contains('video-scale__frame--speelt') ? wissel() : start());

  let neer = null;

  frame.addEventListener('pointerdown', (event) => {
    // De balk onderin doet zijn eigen werk. Zonder deze uitzondering zou het vastzetten
    // hieronder het slepen aan de tijdlijn onderscheppen.
    if (event.target.closest('.player__bar')) {
      return;
    }

    neer = { x: event.clientX, y: event.clientY };

    // Vanaf hier komt het loslaten sowieso bij dit kader binnen, ook als het onder de
    // aanwijzer vandaan schuift. Lukt het vastzetten niet, dan is dat geen ramp -- dan
    // valt het terug op de gewone route -- maar het mag het afspelen niet meeslepen.
    vastzetten(() => frame.setPointerCapture(event.pointerId));
  });

  frame.addEventListener('pointerup', (event) => {
    if (! neer) {
      return;
    }

    const verschoven = Math.hypot(event.clientX - neer.x, event.clientY - neer.y);

    neer = null;

    // Eerst afspelen en dan pas loslaten: het loslaten is opruimwerk en mag nooit
    // tussen de klik en de video in gaan staan.
    if (verschoven < 12) {
      aanslag();
    }

    vastzetten(() => frame.releasePointerCapture(event.pointerId));
  });

  frame.addEventListener('pointercancel', () => {
    neer = null;
  });

  // Toetsenbord: de <button> doet gewoon wat een knop hoort te doen.
  knop.addEventListener('click', (event) => {
    if (event.detail === 0) {
      aanslag();
    }
  });
}

/**
 * De bediening eraan hangen: dezelfde knoppen als onder de speler in de hero, met dit
 * kader als speler en de meegegeven video als bron. Vandaar het haakje -- welk van de
 * twee elementen de film wordt, blijkt hier pas.
 *
 * Weigert de speler dienst, dan valt het terug op de bediening van de browser: liever de
 * grijze balk van Chrome dan een video die niet te stoppen is.
 *
 * @param {Element} frame
 * @param {HTMLVideoElement} video
 * @return {{play: Function, pause: Function, stop: Function}|null}
 */
function bedien(frame, video) {
  video.hidden = false;
  video.dataset.playerVideo = '';

  const speler = setupPlayer(frame);

  if (! speler) {
    video.controls = true;
    speel(video);

    return null;
  }

  // Via de speler en niet rechtstreeks: die zet het geluid aan, de stand goed en de
  // knoppen in de juiste hoek.
  speler.play();

  return speler;
}

/**
 * Het vastzetten en loslaten van de aanwijzer. Een browser mag hier nee zeggen -- een
 * aanwijzer die er niet meer is bijvoorbeeld -- en dat is opruimwerk, geen fout die de
 * rest van de afhandeling mag stilzetten.
 */
function vastzetten(actie) {
  try {
    actie();
  } catch (fout) {
    // Niets aan de hand: zonder vastgezette aanwijzer werkt de gewone route nog.
  }
}

/**
 * Afspelen en de weigering opvangen. Een browser mag nee zeggen -- dan blijft de video
 * staan met zijn eigen bediening eronder, en dat is precies wat je wilt. De afwijzing
 * hoeft geen fout in de console op te leveren.
 */
function speel(video) {
  const gestart = video.play();

  if (gestart) {
    gestart.catch(() => {});
  }
}

/**
 * De groei zelf.
 */
function groeiMee(root) {
  const frame = root.querySelector('[data-video-scale-frame]');
  const [klein, groot] = root.querySelectorAll('[data-video-scale-slot]');

  if (! frame || ! klein || ! groot) {
    return;
  }

  // Verhuizen voordat er iets gemeten wordt: vanaf hier is de kleine halte het vertrekpunt.
  klein.appendChild(frame);
  root.classList.add('video-scale--actief');

  let tijdlijn = null;

  const bouw = () => {
    if (tijdlijn) {
      tijdlijn.scrollTrigger.kill();
      tijdlijn.kill();
      gsap.set(frame, { clearProps: 'all' });
    }

    tijdlijn = gsap.timeline({
      scrollTrigger: {
        // Van het midden van de kleine halte tot het midden van de brede: precies de
        // afstand die het blok ervoor vrijhoudt.
        trigger: klein,
        start: 'center center',
        endTrigger: groot,
        end: 'center center',
        scrub: 0.25,
      },
    });

    tijdlijn.add(Flip.fit(frame, groot, { duration: 1, ease: 'none' }));
  };

  bouw();

  let wachter = null;

  window.addEventListener('resize', () => {
    clearTimeout(wachter);
    wachter = setTimeout(bouw, 150);
  });
}
